<script setup lang="ts">
import { ref, computed, watch } from "vue"
import axios from "axios";

const props = defineProps<{ metal: string, configId: string }>()

const weight = ref(0)
const selectedPurity = ref(0) // Index of purity
const isCalculating = ref(false)
const resultPrice = ref<number | null>(null)

const metals = {
  xau: {
    purities: ['24K', '18K', '14K'],
    label: 'Oro',
    color: 'gold',
    theme: 'dark',
    accent: '#FBBF24', // Tailwind amber-400
    unit: 'gr'
  },
  xag: {
    purities: ['999', '925', '800'],
    label: 'Plata',
    color: 'silver',
    theme: 'light',
    accent: '#94A3B8', // Tailwind slate-400 (Silver color)
    unit: 'kg'
  }
}

const config = computed(() => {
  const m = props.metal as 'xau' | 'xag'
  return metals[m] || metals.xau
})

watch([weight, selectedPurity, () => props.metal], () => {
  resultPrice.value = null
})

async function calculate() {
  if (weight.value <= 0) return
  
  isCalculating.value = true
  try {
    const baseUrl = 'https://capable-heliotrope-e954ce.netlify.app';
    const { data } = await axios.get(`${baseUrl}/.netlify/functions/get-price`, {
      params: {
        metal: props.metal,
        weight: weight.value
      }
    })

    if (!data.error) {
      const purityLabel = config.value.purities[selectedPurity.value]
      resultPrice.value = Number(data.purities[purityLabel].totalPrice)
    }
  } catch (e) {
    console.error('Error fetching price', e)
  } finally {
    isCalculating.value = false
  }
}
</script>

<template>
  <div class="max-w-2xl mx-auto p-2 sm:p-4 font-sans antialiased">
    <div 
      :class="[
        'overflow-hidden rounded-3xl sm:rounded-[3rem] shadow-2xl transition-all duration-500',
        config.theme === 'dark' ? 'bg-[#0F0F0F] text-white' : 'bg-[#F9F9F9] text-gray-900'
      ]"
    >
      <!-- Main Content Area -->
      <div 
        :class="[
          'rounded-t-3xl sm:rounded-t-[3rem] p-4 sm:p-8 space-y-6 sm:space-y-8',
          config.theme === 'dark' ? 'bg-white text-gray-900' : 'bg-white border-t border-gray-100'
        ]"
      >
        <!-- Header / Title -->
        <div class="text-center space-y-1">
          <h2 class="text-2xl sm:text-3xl font-black tracking-tighter uppercase">
            Calculadora de <span :style="{ color: config.accent }">{{ config.label }}</span>
          </h2>
          <p class="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest">Cotización en tiempo real</p>
        </div>

        <!-- 1. Purity Selection -->
        <div class="space-y-3">
          <label class="block text-sm font-bold text-gray-500 uppercase tracking-tight ml-1">1. Seleccione Pureza</label>
          <div class="grid grid-cols-3 gap-2 sm:gap-3">
            <button
              v-for="(purity, index) in config.purities"
              :key="purity"
              @click="selectedPurity = index"
              :class="[
                'flex items-center justify-center p-2 sm:p-4 rounded-xl sm:rounded-2xl transition-all border-2 group relative cursor-pointer',
                selectedPurity === index 
                  ? 'border-black bg-black text-white' 
                  : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200'
              ]"
            >
              <div class="flex items-center space-x-1 sm:space-x-2">
                <div :class="['w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-sm shadow-sm shrink-0', selectedPurity === index ? 'bg-gray-800' : 'bg-white text-gray-900']">
                  {{ purity.includes('K') ? 'K' : '%' }}
                </div>
                <span class="font-bold text-[10px] sm:text-sm whitespace-nowrap">{{ purity }}</span>
              </div>
              
              <div v-if="selectedPurity === index" class="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow-sm" :style="{ backgroundColor: config.accent }">
                <svg class="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>
              </div>
            </button>
          </div>
        </div>

        <!-- 2. Weight Input -->
        <div class="space-y-3">
          <label class="block text-sm font-bold text-gray-500 uppercase tracking-tight ml-1">2. Introduzca Peso ({{ config.unit }})</label>
          <div class="relative group">
            <input
              v-model.number="weight"
              type="number"
              placeholder="0.00"
              class="w-full bg-gray-50 rounded-xl sm:rounded-2xl py-4 sm:py-5 pl-4 sm:pl-6 pr-12 sm:pr-16 text-lg sm:text-xl font-bold border-2 border-transparent focus:border-gray-200 focus:bg-white focus:outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <div class="absolute inset-y-0 right-0 flex items-center pr-4 sm:pr-6">
              <span class="text-gray-400 font-bold uppercase text-xs sm:text-sm">{{ config.unit }}</span>
            </div>
          </div>
        </div>

        <!-- 3. Calculate Button -->
        <button 
          @click="calculate"
          :disabled="isCalculating || weight <= 0"
          class="w-full flex items-center justify-center space-x-2 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-lg sm:text-xl transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 shadow-lg text-white cursor-pointer disabled:cursor-not-allowed"
          :style="{ backgroundColor: config.accent }"
        >
          <svg v-if="isCalculating" class="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isCalculating ? 'Calculando...' : 'Calcular Precio' }}</span>
        </button>

        <!-- 4. Result Display -->
        <div 
          :class="[
            'rounded-2xl sm:rounded-[2.5rem] p-4 sm:p-8 transition-all duration-300 relative overflow-hidden',
            config.theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-gray-50 border border-gray-100'
          ]"
          v-if="resultPrice !== null"
        >
          <div class="relative z-10 text-center">
            <p :class="['text-xs font-semibold uppercase tracking-widest mb-1 sm:mb-2 opacity-50', config.theme === 'dark' ? 'text-gray-400' : 'text-gray-500']">
              Estimación Final
            </p>
            <div class="flex items-baseline justify-center space-x-1 sm:space-x-2">
              <span class="text-3xl sm:text-5xl font-black" :style="{ color: config.accent }">
                {{ resultPrice.toLocaleString('es-ES', { minimumFractionDigits: 2 }) }}
              </span>
              <span :class="['text-xl sm:text-2xl font-bold', config.theme === 'dark' ? 'text-white/60' : 'text-gray-900/60']">€</span>
            </div>
          </div>
          
          <!-- Decorative background glow -->
          <div v-if="config.theme === 'dark'" class="absolute -right-8 -top-8 w-32 h-32 bg-amber-400/10 blur-3xl rounded-full"></div>
          <div v-else class="absolute -right-8 -top-8 w-32 h-32 bg-slate-400/10 blur-3xl rounded-full"></div>
        </div>
      </div>
    </div>
  </div>
</template>
